/* Conversation Director — a deterministic state machine that keeps a roleplay
 * REALISTIC and PROGRESSING, instead of trusting the persona's base prompt to
 * remember what's happened. It accumulates conversation state and, each turn,
 * emits a natural-language DIRECTIVE the host injects into the LIVE avatar via
 * `notifyContext` (→ na-conversationalContextData). The persona's next line is
 * then constrained by state, not hoped from the prompt.
 *
 * It fixes the failure modes reported on Maya:
 *  - She agreed to a meeting, then later denied the rep had asked to set one up.
 *    → `committed` is STICKY and the phase never regresses; once the rep moves
 *      to schedule, the directive forces a concrete day/time and forbids denial.
 *  - She looped "what makes you different / I've heard it all before."
 *    → answered objections become `settledTopics` the directive forbids re-raising.
 *  - She shrugged off a CEO referral (unrealistic).
 *    → high-weight credibility signals are detected and the directive makes them move her.
 *  - She invented the clock ("18… 15… 18…").
 *    → the directive carries the REAL remaining minutes from the host's timer.
 *
 * Pure + framework-generic: behavior comes from the per-vertical DirectorConfig,
 * so it's unit-testable and reused across verticals. Runs client-side off the
 * classifier signals the app already receives each turn (no new service).
 */

export type Phase =
  | 'opening'
  | 'discovery'
  | 'pain'
  | 'proof'
  | 'committed'
  | 'scheduling'
  | 'closed';

const PHASE_RANK: Record<Phase, number> = {
  opening: 0,
  discovery: 1,
  pain: 2,
  proof: 3,
  committed: 4,
  scheduling: 5,
  closed: 6,
};

export interface DirectorState {
  phase: Phase;
  /** A forward-commitment milestone landed — STICKY, can never be unset. */
  committed: boolean;
  /** The rep has explicitly moved to set a concrete next step (date/time/agenda). */
  schedulingRequested: boolean;
  /** Objections/questions the rep has substantively answered — never re-raise. */
  settledTopics: string[];
  /** High-weight credibility signals the rep has delivered (ids from config). */
  credibility: string[];
  turnCount: number;
  /** The latest input looked like background noise / a misfire (empty, a stray
   *  "hello" from a throat-clear, a single filler word). Drives a directive
   *  telling the persona NOT to treat it as a greeting or a fresh start. */
  lastInputNoise: boolean;
}

/** Loader VAD sometimes feeds a phantom turn — an empty string, punctuation, or
 *  a stray "hello"/"mm" from a cough — which made Maya re-greet and restart. We
 *  can't stop the mic from hearing it (loader-side), but we can flag it so the
 *  directive tells the persona to ignore it. Conservative on purpose: only
 *  empty/punctuation, or a 1–2 word greeting/filler, counts as noise. */
const NOISE_FILLER =
  /^(hi|hi there|hello|hey|mm+|hmm+|uh+h?|ah+|erm|ok|okay|yeah|yep|nope|sure|cool|haha+|hah)\W*$/i;
function isLikelyNoise(text: string): boolean {
  const t = (text ?? '').trim();
  if (t === '' || /^[\W_]+$/.test(t)) return true; // empty, "-", ".", etc.
  return t.split(/\s+/).length <= 2 && NOISE_FILLER.test(t);
}

/** What the host knows after a turn: the rep's words + the classifier's read. */
export interface TurnInput {
  producerText: string;
  /** The persona's OWN reply this turn, when available (avatar answerText).
   *  Ground truth for commitment: the buyer saying "fine, set it up / send me
   *  an agenda" is a firmer signal than the classifier tagging the rep's ask. */
  personaText?: string | null;
  milestoneTag: string | null;
  /** Objection the persona raised this turn, if any (classifier objection_label). */
  objectionLabel: string | null;
  /** Whether the rep's move landed substance this turn. */
  yieldQuality: 'earned' | 'hollow' | null;
  isKeyMoment: boolean;
}

export interface CredibilityCue {
  id: string;
  /** Imperative phrasing injected into the directive when this signal fires. */
  directive: string;
  test: RegExp;
}

export interface DirectorConfig {
  /** Milestone tags that mean the persona gave a forward commitment. */
  commitTags: string[];
  /** Rep asking to lock a concrete next step (date/time/agenda/availability). */
  schedulingCue: RegExp;
  /** The PERSONA accepting a next step in their own words ("fine, set it up",
   *  "send me an agenda", "I'll bring my engineer"). When this matches the
   *  persona's reply, commitment is locked from ground truth — independent of
   *  whether the classifier credited the rep's (often hedged) ask. */
  acceptanceCue?: RegExp;
  /** Rep moves the classifier doesn't tag but that should move the persona. */
  credibilityCues: CredibilityCue[];
  /** Total roleplay length in seconds, for the remaining-time directive. */
  sessionSeconds: number;
  /** Whether to inject the "X minutes remain / get impatient" directive. Only
   *  right for time-boxed personas (Maya's 20-min meeting, Wolfgang's 10). For
   *  personas who aren't on a clock (a buyer being represented, an annual
   *  review), leave it off so we don't nudge them to invent time pressure.
   *  Defaults to ON when omitted, so existing time-boxed configs are unchanged. */
  announceTimeRemaining?: boolean;
}

export function initDirectorState(): DirectorState {
  return {
    phase: 'opening',
    committed: false,
    schedulingRequested: false,
    settledTopics: [],
    credibility: [],
    turnCount: 0,
    lastInputNoise: false,
  };
}

const advance = (cur: Phase, next: Phase): Phase =>
  PHASE_RANK[next] > PHASE_RANK[cur] ? next : cur;

/** Fold one turn into the state. Monotonic: phase + sticky flags only move forward. */
export function reduce(
  state: DirectorState,
  input: TurnInput,
  config: DirectorConfig,
): DirectorState {
  const next: DirectorState = {
    ...state,
    settledTopics: [...state.settledTopics],
    credibility: [...state.credibility],
    turnCount: state.turnCount + 1,
  };

  const text = input.producerText ?? '';
  next.lastInputNoise = isLikelyNoise(text);

  // Phase from the milestone the rep just earned (generic vocab + rich packs alike).
  const tag = input.milestoneTag ?? '';
  if (/discover|needs|relationship_reviewed|cold_open/.test(tag))
    next.phase = advance(next.phase, 'discovery');
  if (/pain|budget|goal|issue/.test(tag)) next.phase = advance(next.phase, 'pain');
  if (/proof|data|value|comparable|claims_advocacy|ecosystem|recommendation/.test(tag))
    next.phase = advance(next.phase, 'proof');

  // Commitment is sticky — set it from EITHER the classifier crediting the
  // rep's move OR (more reliably) the persona accepting in their own words.
  // The latter catches the common case where the rep's ask was hedged ("do you
  // think we could maybe get set up?") so the classifier didn't tag a commit,
  // yet the buyer said "fine, set it up." Without this, the persona would later
  // deny ever having agreed.
  const personaAccepted = Boolean(
    config.acceptanceCue && input.personaText && config.acceptanceCue.test(input.personaText),
  );
  if (config.commitTags.includes(tag) || personaAccepted) {
    next.committed = true;
    next.phase = advance(next.phase, 'committed');
  }

  // Rep moved to schedule a concrete next step.
  if (config.schedulingCue.test(text)) {
    next.schedulingRequested = true;
    if (next.committed) next.phase = advance(next.phase, 'scheduling');
  }

  // An objection the rep answered with substance becomes settled (anti-loop).
  if (
    input.objectionLabel &&
    (input.yieldQuality === 'earned' || input.isKeyMoment || input.milestoneTag)
  ) {
    if (!next.settledTopics.includes(input.objectionLabel))
      next.settledTopics.push(input.objectionLabel);
  }

  // High-weight credibility signals (CEO referral, referenceable customers).
  for (const cue of config.credibilityCues) {
    if (cue.test.test(text) && !next.credibility.includes(cue.id)) {
      next.credibility.push(cue.id);
      // Credibility answers "why you" — the differentiation loop is settled.
      if (!next.settledTopics.includes('differentiation'))
        next.settledTopics.push('differentiation');
    }
  }

  return next;
}

/** Whole minutes remaining (never negative). */
export function minutesRemaining(elapsedSeconds: number, config: DirectorConfig): number {
  return Math.max(0, Math.ceil((config.sessionSeconds - elapsedSeconds) / 60));
}

/**
 * The per-turn steering injected into the live avatar. Natural-language imperative
 * so the dialog LLM follows it even when the base prompt wasn't authored for it
 * (e.g. cloned avatars we can't edit). Returns null only before anything's happened.
 */
export function composeDirective(
  state: DirectorState,
  elapsedSeconds: number,
  config: DirectorConfig,
): string {
  const mins = minutesRemaining(elapsedSeconds, config);
  const lines: string[] = [];

  // Real clock — replaces the persona inventing times. Only for time-boxed
  // personas; off by default-when-set-false so non-timed verticals (real estate,
  // an annual review) aren't nudged into inventing a countdown.
  if (config.announceTimeRemaining !== false) {
    const impatience =
      mins <= 2
        ? " You're nearly out of time and visibly impatient — push to wrap with a concrete next step."
        : mins <= 5
          ? " Time is getting short and you're starting to show mild impatience."
          : '';
    lines.push(
      `About ${mins} minute${mins === 1 ? '' : 's'} remain in this meeting; if you mention time, use this number.${impatience}`,
    );
  }

  // Anti-reset: after the meeting is underway the persona must never restart it.
  // Whisper can mis-hear a cough/throat-clear as "hello", which used to make
  // Maya re-greet and re-run discovery from the top. This forbids that.
  if (state.turnCount >= 1) {
    lines.push(
      'This meeting is already underway — you have already greeted the rep and introduced yourself. Do NOT greet again, re-introduce yourself, or restart the conversation from the top; continue from where you are.',
    );
  }

  // Noise/misfire: the latest input looked like a stray "hello" or background
  // noise. Tell the persona to ignore it rather than treat it as a new opening.
  if (state.lastInputNoise && state.turnCount >= 1) {
    lines.push(
      'The rep\'s latest input looks like background noise or a misfire (it is empty or just a stray "hello"/filler word) — do NOT treat it as a greeting or a new question. Stay exactly where you were in the conversation; either continue your current point or briefly ask them to repeat. Never reset.',
    );
  }

  // Progression: once committed, you MUST move the next step forward — never reset.
  if (state.committed && state.schedulingRequested) {
    lines.push(
      'You have ALREADY agreed to this next step — you said yes and even named your terms. The rep is now setting it up: ' +
        'propose a specific day and time, or tell them to send the written agenda and you will confirm availability. ' +
        'You MAY insist your terms are met (the right people attend, an agenda up front), but do NOT deny that you agreed, ' +
        'do NOT say you are "not committing yet," do NOT demand they re-justify whether to meet, and do NOT claim they never asked.',
    );
  } else if (state.committed) {
    lines.push(
      'You have already agreed to a next step and named your terms. Stay consistent: when the rep moves to schedule, lock in a concrete day/time. ' +
        'You may hold them to your terms (who attends, an agenda), but never walk the agreement back, never say you are "not committing," and never re-open whether to meet at all.',
    );
  }

  // Anti-loop: settled topics are closed.
  if (state.settledTopics.length) {
    lines.push(
      `These are already answered — do NOT raise them again or repeat the same line; acknowledge briefly and move forward: ${state.settledTopics.join(', ')}.`,
    );
  }

  // Credibility: high-weight signals must move you.
  for (const cue of config.credibilityCues) {
    if (state.credibility.includes(cue.id)) lines.push(cue.directive);
  }

  return lines.join(' ');
}
